import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { apiJson } from "../../shared/lib/api-client.ts";
import { ensureFirebaseAuth, signInWithEmailAndPassword } from "../lib/firebase.ts";

interface LoginViewProps {
  authError?: string | null;
}

function getLoginErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "code" in error) {
    const code = String((error as { code?: string }).code ?? "");
    if (
      code === "auth/invalid-credential" ||
      code === "auth/user-not-found" ||
      code === "auth/wrong-password"
    ) {
      return "Invalid email or password.";
    }
    if (code === "auth/too-many-requests") {
      return "Too many attempts. Try again in a few minutes.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Unable to sign in. Try again.";
}

function readInitialLoginState() {
  if (typeof window === "undefined") {
    return { email: "", passwordResetSuccess: false };
  }

  const params = new URLSearchParams(window.location.search);
  const email = params.get("email")?.trim() ?? "";
  const passwordResetSuccess = params.get("passwordReset") === "success";

  return { email, passwordResetSuccess };
}

export function LoginView({ authError = null }: LoginViewProps) {
  const initialState = useMemo(() => readInitialLoginState(), []);
  const [email, setEmail] = useState(initialState.email);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(authError);
  const [info, setInfo] = useState<string | null>(
    initialState.passwordResetSuccess ? "Password updated. Log in with your new password." : null
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!initialState.email && !initialState.passwordResetSuccess) return;
    window.history.replaceState(null, "", "/");
  }, [initialState.email, initialState.passwordResetSuccess]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting || authError) return;

    setSubmitting(true);
    setError(null);
    setInfo(null);

    try {
      const auth = ensureFirebaseAuth();
      const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
      await credential.user.getIdToken(true);
    } catch (submitError) {
      setError(getLoginErrorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleForgotPassword() {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || resetting) return;

    setResetting(true);
    setError(null);

    try {
      const response = await apiJson<{ data?: { message?: string } }>(
        "/v1/auth/password-reset/request",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: normalizedEmail }),
        }
      );
      setInfo(
        response.data?.message ??
          "If an account exists for that email, a reset link will arrive shortly."
      );
    } catch (resetError) {
      setError(resetError instanceof Error ? resetError.message : "Unable to send reset email.");
    } finally {
      setResetting(false);
    }
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-eyebrow">Legion Scout</div>
        <h1 className="login-title">Access Terminal</h1>
        <p className="login-subtitle">Invite-only access. Use your provisioned account.</p>

        <form className="login-form" onSubmit={handleSubmit}>
          <label className="login-label" htmlFor="login-email">
            Email
          </label>
          <input
            id="login-email"
            className="login-input"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <label className="login-label" htmlFor="login-password">
            Password
          </label>
          <input
            id="login-password"
            className="login-input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          {info && <div className="login-success">{info}</div>}
          {error && <div className="login-error">{error}</div>}

          <button
            type="submit"
            className="btn login-submit"
            disabled={submitting || Boolean(authError)}
          >
            {submitting ? "Signing In..." : "Log In"}
          </button>

          <button
            type="button"
            className="btn btn-secondary login-secondary"
            onClick={() => {
              void handleForgotPassword();
            }}
            disabled={resetting || !email.trim()}
          >
            {resetting ? "Sending Reset..." : "Forgot Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
